import { Card, CardBody, CardHeader, Skeleton } from '@heroui/react'
import { BarChart3 } from 'lucide-react'
import { useMemo } from 'react'
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'

export default function VariableExtractionChart({ variables, isLoading }) {
  // Transform data for the bar chart
  const chartData = useMemo(() => {
    if (!variables?.length)
      return []

    return variables.map(variable => ({
      name: variable.variable_name,
      successful: variable.successful_extractions,
      null: variable.null_extractions,
      total: variable.total_calls,
      rate: (variable.extraction_rate * 100).toFixed(1),
    }))
  }, [variables])

  // Calculate the maximum value for Y-axis
  const maxYValue = useMemo(() => {
    if (!variables?.length)
      return 100
    return Math.max(...variables.map(v => v.total_calls))
  }, [variables])

  if (isLoading) {
    return <Skeleton className="w-full h-96 rounded-none" />
  }

  if (!chartData.length) {
    return (
      <Card classNames={{
        base: 'border-2 border-gray-200 shadow-none rounded-none',
      }}
      >
        <CardBody>
          <div className="w-full h-full flex flex-col items-center my-24">
            <h1 className="text-2xl text-gray-400 tracking-tighter font-semibold mb-6">
              No extraction data yet.
            </h1>
          </div>
        </CardBody>
      </Card>
    )
  }

  return (
    <Card classNames={{
      base: 'border-2 border-gray-200 w-full shadow-none rounded-none',
    }}
    >
      <CardHeader className="flex justify-between items-center border-b border-gray-200">
        <div>
          <h3 className="text-lg font-taurus font-semibold">Variable Extraction Performance</h3>
          <p className="text-sm text-gray-500">Successful vs null extractions per variable</p>
        </div>
        <div className="flex items-center gap-4 pr-4">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-green-500" />
            <span className="text-xs font-mono">Successful</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-red-500" />
            <span className="text-xs font-mono">Null</span>
          </div>
        </div>
      </CardHeader>
      <CardBody>
        <ResponsiveContainer width="100%" height={350}>
          <BarChart
            data={chartData}
            margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 60,
            }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="#e5e5e5"
              vertical={false}
            />
            <XAxis
              dataKey="name"
              angle={-45}
              textAnchor="end"
              height={100}
              style={{ fontSize: '12px', fontFamily: 'ui-monospace, monospace' }}
              tickLine={false}
            />
            <YAxis
              domain={[0, maxYValue]}
              style={{ fontSize: '12px', fontFamily: 'ui-monospace, monospace' }}
              tickLine={false}
              axisLine={false}
            />
            <Tooltip
              content={({ active, payload, label }) => {
                if (active && payload && payload.length) {
                  const data = payload[0].payload
                  return (
                    <div className="bg-white border-2 border-gray-200 p-3 shadow-none">
                      <p className="font-mono font-medium text-sm mb-2">{label}</p>
                      <div className="space-y-1 text-xs font-mono">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 bg-green-500" />
                          <span className="text-gray-500">Successful:</span>
                          <span className="font-medium">{data.successful}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 bg-red-500" />
                          <span className="text-gray-500">Null:</span>
                          <span className="font-medium">{data.null}</span>
                        </div>
                        <div className="flex items-center gap-2 pt-1 border-t border-gray-200">
                          <span className="text-gray-500">Extraction Rate:</span>
                          <span className="font-medium">
                            {data.rate}
                            %
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500">Total Calls:</span>
                          <span className="font-medium">{data.total}</span>
                        </div>
                      </div>
                    </div>
                  )
                }
                return null
              }}
              cursor={false}
            />
            <Bar
              dataKey="successful"
              name="Successful"
              stackId="extraction"
              fill="#10b981"
              radius={[0, 0, 0, 0]}
            />
            <Bar
              dataKey="null"
              name="Null"
              stackId="extraction"
              fill="#ef4444"
              radius={[0, 0, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </CardBody>
    </Card>
  )
}
